import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  ColorPicker,
  message,
  Space,
  Typography,
  Empty,
  Select,
  Switch,
  Dropdown,
  Drawer,
  Tooltip,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import axios from '../../utils/axios';
import { showApiError } from '../../utils/apiError';
import { formatDate } from '../../utils/dateFormat';
import {
  DISPLAY_TIER_ORDER,
  FIXED_TIER_CONFIG,
  mergePricingTiersForDisplay,
} from '../../utils/pricingTiers';
import {
  PageHeader,
  TableCard,
  Section,
  StatusTag,
  MODAL_WIDTH,
  DRAWER_WIDTH,
  formModalProps,
  tableDefaults,
} from '../../components/admin';

const { Text } = Typography;
const { TextArea } = Input;

/** "5% off", "20% markup" or "No markup" for a price multiplier. */
const multiplierEffect = (multiplier) => {
  const value = Number(multiplier);
  if (!Number.isFinite(value)) return '';
  const discount = Math.round((1 - value) * 1000) / 10;
  if (discount > 0) return `${discount}% off`;
  if (discount < 0) return `${Math.abs(discount)}% markup`;
  return 'No markup';
};

const formatMultiplier = (multiplier) => {
  const value = Number(multiplier);
  return Number.isFinite(value) ? `${value.toFixed(3)}x` : '-';
};

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** The tier's identifier colour; the colour itself is tier data. */
const TierSwatch = ({ color, size = 14 }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block',
      width: size,
      height: size,
      borderRadius: 4,
      background: color,
      border: '1px solid var(--c-border)',
      flex: '0 0 auto',
      verticalAlign: 'middle',
    }}
  />
);

const PricingTierManagement = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [clientListVisible, setClientListVisible] = useState(false);
  const [migrateModalVisible, setMigrateModalVisible] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierToDelete, setTierToDelete] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [clientsTier, setClientsTier] = useState(null);
  const [selectedTierClients, setSelectedTierClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [form] = Form.useForm();
  const [migrateForm] = Form.useForm();

  // Fetch pricing tiers list
  const fetchTiers = useCallback(async (showInactive = includeInactive) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `/admin/pricing-tiers${showInactive ? '?includeInactive=true' : ''}`
      );
      const rawTiers = Array.isArray(response.data) ? response.data : [];
      setTiers(mergePricingTiersForDisplay(rawTiers));
    } catch (error) {
      console.error('Failed to fetch pricing tiers:', error);
      showApiError(error, 'Failed to load pricing tiers');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchTiers(includeInactive);
  }, [includeInactive, fetchTiers]);

  // Fetch clients for a specific tier
  const fetchTierClients = async (tierNameOrId, isForMigration = false) => {
    setClientsLoading(true);
    try {
      let response;
      if (isForMigration) {
        // Detailed client info for migration
        response = await axios.get(`/admin/pricing-tiers/${tierNameOrId}/clients`);
        setSelectedTierClients(response.data.clients || []);
      } else {
        // Original simple client list
        response = await axios.get(`/admin/clients?tier=${tierNameOrId}&status=approved`);
        setSelectedTierClients(response.data.clients || []);
        setClientListVisible(true);
      }
    } catch (error) {
      console.error('Failed to fetch tier clients:', error);
      showApiError(error, 'Failed to load clients for this tier');
    } finally {
      setClientsLoading(false);
    }
  };

  // Handle create/edit
  const handleSubmit = async (values) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Extract hex color from color picker object
      let colorValue = '#1890ff'; // default color
      if (values.color) {
        if (typeof values.color === 'string') {
          colorValue = values.color;
        } else if (values.color.metaColor && values.color.metaColor.isValid) {
          // Convert RGB to hex
          const { r, g, b } = values.color.metaColor;
          colorValue = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        } else if (values.color.toHexString) {
          colorValue = values.color.toHexString();
        }
      }

      const submitData = {
        ...values,
        color: colorValue,
        name: editingTier ? editingTier.name : values.tierKey
      };
      const tierPreset = FIXED_TIER_CONFIG[submitData.name];
      if (tierPreset) {
        submitData.displayName = tierPreset.displayName;
        submitData.multiplier = tierPreset.multiplier;
        submitData.description = tierPreset.description;
      }

      if (editingTier) {
        // Edit
        await axios.put(`/admin/pricing-tiers/${editingTier._id}`, submitData);
        message.success('Pricing tier updated');
      } else {
        // Create
        await axios.post('/admin/pricing-tiers', submitData);
        message.success('Pricing tier created');
      }

      setModalVisible(false);
      setEditingTier(null);
      form.resetFields();
      fetchTiers(includeInactive);
    } catch (error) {
      console.error('Submit error:', error);
      showApiError(error, editingTier ? 'Failed to update pricing tier' : 'Failed to create pricing tier');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete with migration option
  const handleDelete = async (tierId) => {
    try {
      await axios.delete(`/admin/pricing-tiers/${tierId}`);
      message.success('Pricing tier deleted');
      fetchTiers(includeInactive);
    } catch (error) {
      console.error('Delete error:', error);
      const errorMessage = error.response?.data?.message;

      // If cannot delete due to client usage, show migration option
      if (errorMessage && errorMessage.includes('clients are currently using this tier')) {
        const tier = tiers.find(t => t._id === tierId);
        setTierToDelete(tier);
        setMigrateModalVisible(true);
      } else {
        showApiError(error, 'Failed to delete pricing tier');
      }
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    await handleDelete(deleteTarget._id);
    setDeleting(false);
    setDeleteTarget(null);
  };

  // Handle client migration
  const handleMigrateClients = async (values) => {
    setMigrateLoading(true);
    try {
      const response = await axios.post(`/admin/pricing-tiers/${tierToDelete._id}/migrate-clients`, {
        targetTier: values.targetTier
      });

      message.success(response.data.message);

      // After migration success, try delete original tier
      try {
        await axios.delete(`/admin/pricing-tiers/${tierToDelete._id}`);
        message.success('Original pricing tier deleted');
      } catch (deleteError) {
        console.error('Delete after migration error:', deleteError);
        message.warning('Clients were moved, but the original tier could not be deleted');
      }

      setMigrateModalVisible(false);
      setTierToDelete(null);
      migrateForm.resetFields();
      fetchTiers(includeInactive);
    } catch (error) {
      console.error('Migration error:', error);
      showApiError(error, 'Failed to move clients');
    } finally {
      setMigrateLoading(false);
    }
  };

  // Open edit modal
  const handleEdit = (tier) => {
    setEditingTier(tier);
    form.setFieldsValue({
      displayName: tier.displayName,
      multiplier: tier.multiplier,
      description: tier.description,
      color: tier.color
    });
    setModalVisible(true);
  };

  const openCreateModalForTier = (tierName = 'mark-up-1-2') => {
    const preset = FIXED_TIER_CONFIG[tierName] || FIXED_TIER_CONFIG['mark-up-1-2'];

    setEditingTier(null);
    form.setFieldsValue({
      tierKey: tierName,
      displayName: preset.displayName,
      multiplier: preset.multiplier,
      description: preset.description,
      color: preset.color
    });
    setModalVisible(true);
  };

  // Open create modal
  const handleCreate = () => {
    const existingTierNames = new Set(tiers.map((tier) => tier.name));
    const firstMissingTier = DISPLAY_TIER_ORDER.find((tierName) => !existingTierNames.has(tierName));
    if (!firstMissingTier) {
      message.info('All default pricing tiers already exist. Please edit existing tiers.');
      return;
    }
    openCreateModalForTier(firstMissingTier);
  };

  const closeTierModal = () => {
    setModalVisible(false);
    setEditingTier(null);
    form.resetFields();
  };

  const closeClientList = () => {
    setClientListVisible(false);
    setSelectedTierClients([]);
  };

  const closeMigrate = () => {
    setMigrateModalVisible(false);
    setTierToDelete(null);
    migrateForm.resetFields();
  };

  const existingTierNames = new Set(tiers.map((tier) => tier.name));
  const allTiersExist = DISPLAY_TIER_ORDER.every((tierName) => existingTierNames.has(tierName));
  const totalClients = tiers.reduce((sum, t) => sum + (t.clientCount || 0), 0);
  const deleteClientCount = deleteTarget?.clientCount || 0;

  // Table columns definition
  const columns = [
    {
      title: 'Tier',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (text, record) => (
        <Space size={10} align="center">
          <TierSwatch color={record.color} />
          <div>
            <div className="cell-main">{text}</div>
            <div className="cell-sub cell-mono">{record.name}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Multiplier',
      dataIndex: 'multiplier',
      key: 'multiplier',
      width: 130,
      align: 'right',
      render: (multiplier) => (
        <div>
          <div className="cell-money">{formatMultiplier(multiplier)}</div>
          <div className="cell-sub">{multiplierEffect(multiplier)}</div>
        </div>
      ),
    },
    {
      title: 'Clients',
      dataIndex: 'clientCount',
      key: 'clientCount',
      width: 110,
      align: 'right',
      render: (count, record) =>
        count > 0 ? (
          <Tooltip title="Show the clients on this tier">
            <Button
              type="link"
              size="small"
              icon={<UserOutlined />}
              loading={clientsLoading && clientsTier?.name === record.name}
              onClick={() => {
                setClientsTier(record);
                fetchTierClients(record.name);
              }}
            >
              {count}
            </Button>
          </Tooltip>
        ) : (
          <span className="muted">0</span>
        ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      render: (description) =>
        description ? (
          <Text ellipsis={{ tooltip: description }} style={{ maxWidth: 280 }}>
            {description}
          </Text>
        ) : (
          <span className="muted">No description</span>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 110,
      render: (isActive) => (
        <StatusTag tone={isActive ? 'success' : 'default'}>{isActive ? 'Active' : 'Inactive'}</StatusTag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: formatDate,
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      fixed: 'right',
      align: 'right',
      render: (_, record) => (
        <span className="table-actions">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [{ key: 'delete', label: 'Delete tier', icon: <DeleteOutlined />, danger: true }],
              onClick: ({ key }) => {
                if (key === 'delete') setDeleteTarget(record);
              },
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              aria-label={`More actions for ${record.displayName}`}
            />
          </Dropdown>
        </span>
      ),
    },
  ];

  const clientColumns = [
    {
      title: 'Client',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (companyName, client) => (
        <div>
          <div className="cell-main">{companyName || '-'}</div>
          <div className="cell-sub">{client.email}</div>
        </div>
      ),
    },
    { title: 'Country', dataIndex: 'country', key: 'country', width: 100, render: (v) => v || '-' },
    { title: 'Currency', dataIndex: 'currency', key: 'currency', width: 100, render: (v) => v || '-' },
    { title: 'Joined', dataIndex: 'createdAt', key: 'createdAt', width: 120, render: formatDate },
  ];

  const createButton = (
    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} disabled={allTiersExist}>
      Create tier
    </Button>
  );

  return (
    <div>
      <PageHeader
        title="Pricing tiers"
        subtitle="Each client is on one tier. Their voucher prices are the settlement price times the tier's multiplier."
        extra={
          allTiersExist ? (
            <Tooltip title="All four standard tiers already exist. Edit one instead.">
              <span>{createButton}</span>
            </Tooltip>
          ) : (
            createButton
          )
        }
      />

      <TableCard
        title="Tiers"
        count={tiers.length}
        extra={
          <Space size={16} wrap>
            <span className="muted">{plural(totalClients, 'client')} assigned</span>
            <Space size={8}>
              <Switch
                id="pricing-tiers-show-inactive"
                size="small"
                checked={includeInactive}
                onChange={setIncludeInactive}
              />
              <label htmlFor="pricing-tiers-show-inactive">Show inactive</label>
            </Space>
          </Space>
        }
      >
        <Table
          {...tableDefaults}
          columns={columns}
          dataSource={tiers}
          rowKey={(record) => record._id || record.name}
          loading={loading}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={includeInactive ? 'No pricing tiers yet' : 'No active pricing tiers'}
              />
            ),
          }}
        />
      </TableCard>

      {/* Clients on a tier */}
      <Drawer
        title={clientsTier ? `Clients on ${clientsTier.displayName}` : 'Clients on this tier'}
        open={clientListVisible}
        onClose={closeClientList}
        width={DRAWER_WIDTH.md}
        destroyOnHidden
      >
        <Section
          flush
          description={
            clientsTier && selectedTierClients.length !== (clientsTier.clientCount || 0)
              ? `Showing ${plural(selectedTierClients.length, 'approved client')}. The tier count (${clientsTier.clientCount}) covers every active account on the tier, so the numbers can differ.`
              : 'Approved clients currently priced on this tier.'
          }
        >
          <Table
            size="small"
            columns={clientColumns}
            dataSource={selectedTierClients}
            rowKey={(client) => client._id || client.id || client.email}
            loading={clientsLoading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{
              emptyText: (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No clients found on this tier" />
              ),
            }}
          />
        </Section>
      </Drawer>

      {/* Create / edit tier */}
      <Modal
        {...formModalProps}
        title={editingTier ? `Edit ${editingTier.displayName} tier` : 'Create pricing tier'}
        open={modalVisible}
        onCancel={closeTierModal}
        width={MODAL_WIDTH.md}
        footer={[
          <Button key="cancel" onClick={closeTierModal} disabled={submitting}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitting}
            disabled={submitting}
            onClick={() => form.submit()}
          >
            {editingTier ? 'Save changes' : 'Create tier'}
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={(changedValues, allValues) => {
            if (editingTier || !changedValues.tierKey) return;
            const preset = FIXED_TIER_CONFIG[changedValues.tierKey];
            if (!preset) return;
            form.setFieldsValue({
              displayName: preset.displayName,
              multiplier: preset.multiplier,
              description: preset.description,
              color: preset.color
            });
          }}
          requiredMark={false}
        >
          <Section
            title="Tier"
            description={
              editingTier
                ? `Tier key: ${editingTier.name}`
                : 'Pick one of the standard tiers. Its preset values fill in below.'
            }
          >
            <Row gutter={16}>
              {!editingTier && (
                <Col xs={24} md={14}>
                  <Form.Item
                    name="tierKey"
                    label="Standard tier"
                    rules={[{ required: true, message: 'Please select tier key' }]}
                  >
                    <Select placeholder="Choose a standard tier">
                      {DISPLAY_TIER_ORDER.map((tierName) => (
                        <Select.Option key={tierName} value={tierName}>
                          {FIXED_TIER_CONFIG[tierName].displayName}
                          {existingTierNames.has(tierName) ? (
                            <span className="muted"> · already exists</span>
                          ) : null}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} md={editingTier ? 24 : 10}>
                <Form.Item
                  name="color"
                  label="Identifier color"
                  initialValue="#1890ff"
                  extra="Each tier needs its own color."
                  rules={[
                    { required: true, message: 'Please select a color' },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();

                        const existingColors = tiers
                          .filter(tier => editingTier ? tier._id !== editingTier._id : true)
                          .map(tier => tier.color?.toLowerCase());

                        // Extract hex color from color picker object if needed
                        let colorValue = value;
                        if (typeof value === 'object' && value.metaColor) {
                          const { r, g, b } = value.metaColor;
                          colorValue = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                        } else if (typeof value === 'object' && value.toHexString) {
                          colorValue = value.toHexString();
                        }

                        if (existingColors.includes(colorValue?.toLowerCase())) {
                          return Promise.reject(new Error('This color is already used by another tier'));
                        }

                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <ColorPicker
                    showText
                    format="hex"
                    onChange={(color) => {
                      // Convert color object to hex string for form storage
                      const hex = color.toHexString();
                      form.setFieldValue('color', hex);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Section>

          <Section
            title="Pricing preset"
            description="Standard tiers always save with their preset name, multiplier and description, so these fields are read-only."
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="displayName"
                  label="Display name"
                  rules={[
                    { required: true, message: 'Please enter display name' },
                    { max: 50, message: 'Name cannot exceed 50 characters' }
                  ]}
                >
                  <Input readOnly variant="filled" placeholder="e.g., Enterprise VIP, Wholesale Partner" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="multiplier"
                  label="Price multiplier"
                  rules={[
                    { required: true, message: 'Please enter price multiplier' },
                    {
                      type: 'number',
                      min: 0.1,
                      max: 2.0,
                      message: 'Multiplier must be between 0.1 and 2.0'
                    }
                  ]}
                  extra="0.800 is a 20% discount, 1.200 a 20% markup."
                >
                  <InputNumber
                    readOnly
                    variant="filled"
                    style={{ width: '100%' }}
                    step={0.001}
                    precision={3}
                    formatter={value => `${value}x`}
                    parser={value => value.replace('x', '')}
                    placeholder="1.000"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="description"
              label="Description"
              rules={[
                { max: 200, message: 'Description cannot exceed 200 characters' }
              ]}
              style={{ marginBottom: 0 }}
            >
              <TextArea
                readOnly
                variant="filled"
                rows={2}
                placeholder="Describe the features or use cases of this tier..."
              />
            </Form.Item>
          </Section>
        </Form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        title="Delete pricing tier"
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        width={MODAL_WIDTH.sm}
        destroyOnHidden
        maskClosable={!deleting}
        closable={!deleting}
        footer={[
          <Button key="cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>,
          <Button key="delete" type="primary" danger loading={deleting} onClick={confirmDelete}>
            Delete tier
          </Button>,
        ]}
      >
        {deleteTarget ? (
          <>
            <p style={{ marginTop: 0 }}>
              <strong>{deleteTarget.displayName}</strong> will be removed from the pricing tiers. This
              can't be undone.
            </p>
            <p className="muted" style={{ marginBottom: 0 }}>
              {deleteClientCount > 0
                ? `${plural(deleteClientCount, 'client')} ${deleteClientCount === 1 ? 'is' : 'are'} on this tier. The delete is refused until they are moved to another tier.`
                : 'No clients are on this tier.'}
            </p>
          </>
        ) : null}
      </Modal>

      {/* Move clients, then delete */}
      <Modal
        {...formModalProps}
        title={tierToDelete ? `Move clients off ${tierToDelete.displayName}` : 'Move clients'}
        open={migrateModalVisible}
        onCancel={closeMigrate}
        width={MODAL_WIDTH.md}
        footer={[
          <Button key="cancel" onClick={closeMigrate} disabled={migrateLoading}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            loading={migrateLoading}
            onClick={() => migrateForm.submit()}
          >
            Move clients and delete tier
          </Button>,
        ]}
      >
        {tierToDelete && (
          <>
            <p style={{ marginTop: 0 }}>
              <strong>{tierToDelete.displayName}</strong> still has clients, so it can't be deleted
              yet. Choose the tier to move them to.
            </p>

            <Form
              form={migrateForm}
              layout="vertical"
              onFinish={handleMigrateClients}
            >
              <Form.Item
                name="targetTier"
                label="Move clients to"
                rules={[{ required: true, message: 'Please select a target tier' }]}
              >
                <Select placeholder="Choose a tier">
                  {tiers
                    .filter(tier => tier._id !== tierToDelete._id && tier.isActive)
                    .map(tier => (
                      <Select.Option key={tier._id} value={tier.name}>
                        <Space size={8}>
                          <TierSwatch color={tier.color} size={12} />
                          <span>{tier.displayName}</span>
                          <span className="muted">
                            {formatMultiplier(tier.multiplier)} · {multiplierEffect(tier.multiplier)} ·{' '}
                            {plural(tier.clientCount || 0, 'client')}
                          </span>
                        </Space>
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Form>

            <ul className="muted" style={{ margin: 0, paddingLeft: 18 }}>
              <li>
                Every client on <strong>{tierToDelete.displayName}</strong> moves to the selected tier
                and is priced with its multiplier from then on.
              </li>
              <li>{tierToDelete.displayName} is deleted once the move succeeds.</li>
              <li>This can't be undone.</li>
            </ul>
          </>
        )}
      </Modal>
    </div>
  );
};

export default PricingTierManagement;
